import { DataGrid } from "@serenity-is/corelib";
import { activeQuickFilters, clearAllQuickFilters } from "./ListPageChrome";

/**
 * The two grid states from handoff section 07 — "Empty grid" and "Loading skeleton".
 *
 * WHAT SERENITY DOES INSTEAD
 * An empty Serenity grid is simply a grid with no rows: the column heads sit above a
 * blank canvas with no explanation, no indication that a *filter* rather than an empty
 * database caused it, and nothing to click. A loading grid is the same blank canvas with
 * a small spinner over it. The handoff draws both as designed panels — the empty state
 * naming the filters that are narrowing the view and offering to clear them, and the
 * loading state as skeleton rows that keep the table's shape so the page does not
 * collapse and reflow when data lands.
 *
 * WHY THIS IS A SEPARATE MODULE FROM ListPageChrome
 * ListPageChrome rearranges controls once, at construction. These two states are
 * reactive — they appear and disappear as the view loads and as filters change — so they
 * need the view's own load events rather than a one-shot pass. Keeping them apart means
 * neither file has to care when the other runs.
 *
 * Like ListPageChrome this hooks the static DataGrid.onAfterInit, so every grid in the
 * application gets both states and no *Grid.ts has to opt in.
 */

const ENHANCED = "m-has-states";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
}

/**
 * The plural noun for the copy — "No franchisees match these filters". Taken from the
 * grid's own title so it is right for every module without a lookup table; falls back to
 * the generic word rather than printing an empty string.
 */
function entityLabel(grid: DataGrid<any>): string {
    const card = grid.domNode;
    const title = card?.parentElement?.querySelector<HTMLElement>(".m-list-head .grid-title")?.textContent
        || card?.querySelector<HTMLElement>(":scope > .grid-title")?.textContent
        || document.querySelector<HTMLElement>(".m-topbar-heading h1")?.textContent
        || "records";
    return title.trim().toLowerCase() || "records";
}

export function installGridStates() {
    DataGrid.onAfterInit.subscribe(e => {
        try { attach(e.dataGrid); }
        catch (err) { console.error("[BWDesignSystem] grid states failed for a grid", err); }
    });
}

function attach(grid: DataGrid<any>) {
    const card = grid?.domNode;
    if (!card || card.classList.contains(ENHANCED)) return;
    card.classList.add(ENHANCED);

    const empty = buildEmpty(grid, card);
    const skeleton = buildSkeleton();

    /**
     * ⚠ These are appended to SleekGrid's OWN container, not to the card.
     *
     * They are absolute overlays, and the card also holds the tools row that
     * ListPageChrome built — search, the Filters toggle, the chips. An overlay stretched
     * across the card would cover those, so the empty state would tell the user to clear
     * filters while sitting on top of the controls that clear them. Anchored to the grid
     * container it covers only the table (column heads included, which is what the
     * handoff draws) and leaves every control above it live.
     *
     * Replacing the canvas instead of overlaying it is not an option either — SleekGrid
     * does not rebuild its viewport from a detached node.
     */
    const host = (grid.slickGrid as any)?.getContainerNode?.()
        || card.querySelector<HTMLElement>(".slick-container")
        || card;
    host.classList.add("m-state-host");
    host.appendChild(skeleton);
    host.appendChild(empty);

    const view: any = grid.view;

    /**
     * ⚠ These marker classes are `m-grid-is-*`, NOT `m-empty` / `m-loading`.
     *
     * `m-empty` is ALREADY TAKEN — DashboardIndex.cshtml uses it for its "No modules are
     * visible" panel, and theme-app.css styles it as a centred flex column with 48px padding.
     * Putting that class on the grid card handed the card the whole panel treatment:
     * `align-items: center` made every child shrink-wrap, so the tools row, the bulk bar
     * and the pager all jumped to the middle of the card the moment a grid went empty.
     * Nothing about the rule was wrong; the class simply meant something else already.
     */
    const showSkeleton = (on: boolean) => {
        skeleton.hidden = !on;
        card.classList.toggle("m-grid-is-loading", on);
        // While the skeleton is up the empty panel must stay down, or a slow first load
        // flashes "no records match" before any request has even answered.
        if (on) empty.hidden = true;
    };

    const refreshEmpty = () => {
        const total = view?.getLength?.() ?? 0;
        const loading = card.classList.contains("m-grid-is-loading");
        const show = !loading && total === 0;
        empty.hidden = !show;
        card.classList.toggle("m-grid-is-empty", show);
        if (show) fillEmptyCopy(grid, card, empty);
    };

    view?.onDataLoading?.subscribe?.(() => showSkeleton(true));
    view?.onDataLoaded?.subscribe?.(() => { showSkeleton(false); refreshEmpty(); });
    grid.onDataChanged?.subscribe?.(() => refreshEmpty());

    refreshEmpty();
}

/**
 * Skeleton rows. The count is deliberately fixed rather than matched to the page size:
 * the point is to hold the table's shape for the eye, and reading the real page size
 * would mean waiting for the very request this is covering for.
 */
function buildSkeleton() {
    const wrap = el("div", "m-skeleton");
    wrap.hidden = true;
    wrap.setAttribute("aria-hidden", "true");
    for (let i = 0; i < 8; i++) {
        const row = el("div", "m-skeleton-row");
        // Uneven widths read as data; eight identical bars read as a broken layout.
        [26, 16, 12, 18, 10, 14].forEach(w => {
            const cell = el("div", "m-skeleton-cell");
            cell.style.width = w + "%";
            row.appendChild(cell);
        });
        wrap.appendChild(row);
    }
    return wrap;
}

function buildEmpty(grid: DataGrid<any>, card: HTMLElement) {
    const wrap = el("div", "m-empty-state");
    wrap.hidden = true;

    wrap.appendChild(el("div", "m-empty-mark"));
    wrap.appendChild(el("div", "m-empty-title"));
    wrap.appendChild(el("div", "m-empty-body"));

    const actions = el("div", "m-empty-actions");

    const clear = el("button", "m-empty-clear", "Clear filters") as HTMLButtonElement;
    clear.type = "button";
    clear.addEventListener("click", () => {
        clearAllQuickFilters(card);
        clearQuickSearch(card);
        // The quick filter bar reacts to the editors' change events asynchronously.
        setTimeout(() => grid.refresh?.(), 0);
    });
    actions.appendChild(clear);

    // The primary action is the page's own New button, cloned rather than rebuilt so it
    // keeps whatever permissions and handler Serenity gave it. Clicking the clone
    // forwards to the real one.
    const create = el("button", "m-empty-create") as HTMLButtonElement;
    create.type = "button";
    actions.appendChild(create);

    wrap.appendChild(actions);
    return wrap;
}

/**
 * Empties the quick search box.
 *
 * ⚠ This is NOT covered by clearAllQuickFilters, which only walks `.quick-filter-item`
 * editors — the quick SEARCH is a separate control that Serenity keeps outside that bar.
 * It has to be cleared here because the empty state counts the search as one of the
 * things "narrowing this view", so a Clear filters button that left the search in place
 * cleared nothing the user could see and the grid stayed empty.
 *
 * ⚠ The value must be set through the native setter, not `input.value = ""`. Serenity's
 * QuickSearchInput tracks its last value to decide whether to re-query; assigning the
 * property directly can leave that internal value stale so the control decides nothing
 * changed and never re-runs the search.
 */
function clearQuickSearch(card: HTMLElement) {
    const input = card.querySelector<HTMLInputElement>(".s-QuickSearchBar input");
    if (!input || !input.value) return;
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(input), "value")?.set;
    setter ? setter.call(input, "") : (input.value = "");
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true, key: "Backspace" }));
}

/**
 * The copy is rewritten every time the state is shown, because it says different things
 * depending on WHY the grid is empty — the whole point of the handoff's version. With
 * filters on it names them and offers to clear; with none it invites the first record.
 */
function fillEmptyCopy(grid: DataGrid<any>, card: HTMLElement, wrap: HTMLElement) {
    const filters = activeQuickFilters(card);
    const search = card.querySelector<HTMLInputElement>(".s-QuickSearchBar input")?.value?.trim();
    const noun = entityLabel(grid);

    const title = wrap.querySelector<HTMLElement>(".m-empty-title")!;
    const body = wrap.querySelector<HTMLElement>(".m-empty-body")!;
    const clear = wrap.querySelector<HTMLButtonElement>(".m-empty-clear")!;
    const create = wrap.querySelector<HTMLButtonElement>(".m-empty-create")!;

    /**
     * ⚠ The opt-in escape hatch, and the reason it has to exist: this function works out WHY a
     * grid is empty by reading the quick filters and the search box, which is everything the
     * PERSON can see — but a grid filtered in code (a detail grid holding its master's id, a grid
     * with a fixed setCriteriaParameter) is narrowed by something with no on-screen control. Such
     * a grid took the "nothing has been added here" branch and told the reader the table was
     * empty when it was not.
     *
     * A grid opts in by implementing `getEmptyState()`, exactly as one opts into a card picture
     * with `getCardImageProperty()`. Only the WORDING is delegated — the markup, the create
     * button and the show/hide logic all stay here, so an opted-in grid still looks like every
     * other empty state.
     */
    const override = (grid as any).getEmptyState?.();

    if (override) {
        title.textContent = override.title;
        body.textContent = override.body;
        // A code-level filter has no control to clear, so offering to clear filters would be a
        // button that provably does nothing.
        clear.hidden = true;
        if (override.hideCreate) {
            create.hidden = true;
            return;
        }
        mirrorCreateButton(card, create);
        return;
    }

    const narrowing = filters.length + (search ? 1 : 0);

    if (narrowing > 0) {
        title.textContent = `No ${noun} match these filters`;
        const parts = filters.slice();
        if (search) parts.push(`Search = "${search}"`);
        const count = narrowing === 1 ? "One filter is" : `${narrowing} filters are`;
        const them = narrowing === 1 ? "it" : "them";
        body.textContent = `${count} narrowing this view: ${parts.join(", ")}. Clear ${them}, or create the first record.`;
        clear.hidden = false;
    } else {
        title.textContent = `No ${noun} yet`;
        body.textContent = `Nothing has been added here. Create the first record to get started.`;
        clear.hidden = true;
    }

    mirrorCreateButton(card, create);
}

/**
 * Mirror the real New button's label and availability; hide the clone entirely if the user has no
 * create permission, since Serenity simply does not render the button then.
 */
function mirrorCreateButton(card: HTMLElement, create: HTMLButtonElement) {
    const real = findCreateButton(card);
    if (real) {
        create.hidden = false;
        create.textContent = real.textContent?.trim() || "New";
        create.onclick = () => real.click();
    } else {
        create.hidden = true;
    }
}

/**
 * The New button has been moved into the page header by ListPageChrome, so it is no
 * longer inside the card — look in the header that sits beside the card as well.
 */
function findCreateButton(card: HTMLElement): HTMLElement | null {
    const scopes = [card.parentElement?.querySelector<HTMLElement>(".m-list-actions"), card];
    for (const scope of scopes) {
        const btn = scope?.querySelector<HTMLElement>(".add-button, .tool-button.add-button");
        if (btn) return btn;
    }
    return null;
}
